import React, { useState, useEffect } from 'react';
import { HiOutlineMail, HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';
import { LuLogIn } from 'react-icons/lu';
import { GoShield } from 'react-icons/go';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { loginUserAsync } from '../../../features/auth/authActions'; 

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [localError, setLocalError] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Read auth state from Redux slice store
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const serverError = useSelector((state) => state.auth.error);
  const isLoading = useSelector((state) => state.auth.isLoading);

  // Handle automatic redirection once slice state reports true
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError('');

    // Basic frontend verification fallback
    if (!email || !password) {
      setLocalError('Please fill out all required authentication fields.');
      return;
    }

    // 3. Dispatch the real credentials to the async API thunk runner
    dispatch(loginUserAsync({ email, password }));
  };

  const activeErrorMessage = localError || serverError;


  return (
    <div className="min-h-screen w-full bg-[#FAFAFC] flex flex-col justify-between items-center p-4 font-sans selection:bg-indigo-500 selection:text-white">
      
      {/* Top / Center Spacer for true vertical alignment */}
      <div className="flex-1 flex items-center justify-center w-full max-w-md xl:max-w-xl xl:py-12">
        
        {/* Main Card Element - Expanded width & padding on large screens */}
        <div className="w-full bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.02)] p-8 md:p-10 xl:p-14 flex flex-col items-center">
          
          {/* Brand Vector Logo Dynamic Display - Scaled for FHD */}
          <div className="w-16 h-16 xl:w-24 xl:h-24 relative flex items-center justify-center mb-6 xl:mb-8">
            <svg viewBox="0 0 100 100" className="w-full h-full" fill="none" xmlns="http://w3.org">
              <path d="M50 10 L15 85 L38 85 L50 55 L62 85 L85 85 Z" fill="url(#logo-grad)" />
              <path d="M50 55 L38 85 L62 85 Z" fill="#311499" opacity="0.15" />
              <defs>
                <linearGradient id="logo-grad" x1="15" y1="10" x2="85" y2="85" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#7C3AED" />
                  <stop offset="1" stopColor="#4F46E5" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          {/* Card Header Typography - Scaled text styles */}
          <h1 className="text-2xl xl:text-4xl font-bold text-gray-900 tracking-tight text-center">Welcome back</h1>
          <p className="text-sm xl:text-lg text-gray-500 mt-1.5 xl:mt-2 text-center">
            Sign in to your <span className="font-semibold text-gray-700">AAZIRA ONE</span> account
          </p>

          {/* Core Input Form Submission Frame */}
          <form onSubmit={handleSubmit} className="w-full mt-8 xl:mt-10 space-y-5 xl:space-y-6">
            
            {/* Local Validation Error Banner */}
            {activeErrorMessage  && (
              <div className="p-3.5 text-xs font-medium text-red-600 bg-red-50 border border-red-100 rounded-xl">
                {activeErrorMessage}
              </div>
            )}

            {/* Email Address Section */}
            <div>
              <label htmlFor="email" className="block text-xs xl:text-sm font-bold text-gray-800 uppercase tracking-wider mb-2">
                Email address
              </label>
              <div className="relative rounded-xl shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3.5 xl:pl-4 flex items-center pointer-events-none text-gray-400">
                  <HiOutlineMail className="w-5 h-5 xl:w-6 xl:h-6" />
                </div>
                <input
                  type="email"
                  id="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="block w-full pl-11 xl:pl-14 pr-4 py-3 xl:py-4 bg-white text-gray-900 placeholder-gray-400 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition-all text-sm xl:text-base"
                />
              </div>
            </div>

            {/* Password Section */}
            <div>
              <label htmlFor="password" className="block text-xs xl:text-sm font-bold text-gray-800 uppercase tracking-wider mb-2">
                Password
              </label>
              <div className="relative rounded-xl shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3.5 xl:pl-4 flex items-center pointer-events-none text-gray-400">
                  <HiOutlineLockClosed className="w-5 h-5 xl:w-6 xl:h-6" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="block w-full pl-11 xl:pl-14 pr-11 xl:pr-14 py-3 xl:py-4 bg-white text-gray-900 placeholder-gray-400 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition-all text-sm xl:text-base"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3.5 xl:pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <HiOutlineEyeOff className="w-5 h-5 xl:w-6 xl:h-6" /> : <HiOutlineEye className="w-5 h-5 xl:w-6 xl:h-6" />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password Anchor Flex Row */}
            <div className="flex items-center justify-between text-sm xl:text-base pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none group text-gray-700 font-medium">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 xl:w-5 xl:h-5 rounded text-indigo-600 border-gray-300 focus:ring-indigo-500/30 accent-indigo-600"
                />
                <span>Remember me</span>
              </label>
              <Link to="/forgot-password" className="font-semibold text-indigo-600 hover:text-indigo-700 transition-colors">
                Forgot password?
              </Link>
            </div>

            {/* Sign In CTA Form Button */}
            <button
              type="submit"
              className="w-full mt-2 bg-[#5822E5] hover:bg-[#491bc4] text-white py-3.5 xl:py-4 px-4 rounded-xl font-semibold shadow-md shadow-indigo-600/10 hover:shadow-indigo-600/20 active:scale-[0.99] transition-all flex items-center justify-center gap-2 text-sm xl:text-base"
            >
              <LuLogIn className="w-4 h-4 xl:w-5 xl:h-5 stroke-[2.5]" />
              <span>Sign In</span>
            </button>
          </form>

          {/* Bottom Security Disclaimer Badge Callout */}
          <div className="w-full mt-8 xl:mt-10 pt-6 xl:pt-8 border-t border-gray-100 flex flex-col items-center text-center">
            <div className="w-8 h-8 xl:w-12 xl:h-12 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 mb-3 xl:mb-4">
              <GoShield className="w-4 h-4 xl:w-6 xl:h-6" />
            </div>
            <h4 className="text-xs xl:text-base font-bold text-gray-800 leading-tight">Access is restricted to authorised users only.</h4>
            <p className="text-[11px] xl:text-sm text-gray-400 mt-1 xl:mt-2 max-w-[280px] xl:max-w-sm leading-normal">
              If you believe you should have access, contact your system administrator.
            </p>
          </div>

        </div>
      </div>

      {/* Global Application System Footer Links */}
      <footer className="w-full max-w-5xl xl:max-w-7xl flex flex-col sm:flex-row items-center justify-between gap-4 py-4 xl:py-6 text-xs xl:text-sm text-gray-400 border-t border-gray-200/50 mt-8">
        <div>&copy; 2026 AAZIRA LTD. All rights reserved.</div>
        <div className="flex items-center gap-6 font-medium">
          <a href="#privacy" className="text-indigo-600/80 hover:text-indigo-700 transition-colors">Privacy Policy</a>
          <a href="#terms" className="text-indigo-600/80 hover:text-indigo-700 transition-colors">Terms of Service</a>
          <a href="#support" className="text-indigo-600/80 hover:text-indigo-700 transition-colors">Support</a>
        </div>
      </footer>

    </div>
  );
}
